import type { JSX } from 'react';

import { Container, Link, Stack, Typography } from '@mui/material';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import LayoutMain from '../components/layout/LayoutMain';
import { InterpolateContent } from '../i18n/i18n';

interface FormatSectionProps {
  content: string;
  title: string;
  titleVariant?: 'body1' | 'body2' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'subtitle1' | 'subtitle2';
}

interface FormatSubSectionProps {
  content: string;
  title: string;
}

interface FormattedSectionsProps {
  formattedSections: Record<string, Record<string, string[]>>;
}

function FormatSection({ content, title, titleVariant = 'h4' }: FormatSectionProps): JSX.Element {
  return (
    <>
      <Typography variant={titleVariant}>{title}</Typography>
      <Typography paragraph>
        <InterpolateContent i18nKey={content} />
      </Typography>
    </>
  );
}

function FormatSubSection({ content, title }: FormatSubSectionProps): JSX.Element {
  return (
    <>
      <strong>{title}</strong>: <InterpolateContent i18nKey={content} />
    </>
  );
}

function FormattedSections({ formattedSections }: FormattedSectionsProps): JSX.Element | null {
  const { t } = useTranslation();

  return (
    formattedSections && (
      <Stack spacing={0} sx={{ 'margin-top': '0px' }}>
        {Object.keys(formattedSections).map((parentSection, indexParent) => (
          <Container disableGutters={true} key={indexParent} maxWidth={false}>
            <FormatSection content={t(`${parentSection}.content`)} title={t(`${parentSection}.title`)} />

            {Object.keys(formattedSections[parentSection]).length > 0 &&
              Object.keys(formattedSections[parentSection]).map((sectionName, indexSection) => (
                <React.Fragment key={indexSection}>
                  <Typography paragraph>
                    <FormatSubSection
                      content={t(`${parentSection}.${sectionName}.content`)}
                      title={`${indexSection + 1}. ${t(`${parentSection}.${sectionName}.title`)}`}
                    />
                    {formattedSections[parentSection][sectionName].length > 0 && (
                      <>
                        <Typography paragraph sx={{ paddingLeft: '8px' }}>
                          {formattedSections[parentSection][sectionName].map((subsection, indexSubsection) => (
                            <Typography key={indexSubsection}>
                              <FormatSubSection
                                content={t(`${parentSection}.${sectionName}.${subsection}.content`)}
                                title={`${indexSection + 1}.${indexSubsection + 1}. ${t(
                                  `${parentSection}.${sectionName}.${subsection}.title`
                                )}`}
                              />
                            </Typography>
                          ))}
                        </Typography>
                      </>
                    )}
                  </Typography>
                </React.Fragment>
              ))}
          </Container>
        ))}
      </Stack>
    )
  );
}

function PrivacyPolicyContent(): JSX.Element {
  const { t } = useTranslation();
  const dataCollectionAndUseSections = {
    'privacy_policy.changes_to_this_privacy_policy': {},
    'privacy_policy.consent': {},
    'privacy_policy.data_collection_and_use': {
      automatically_collected_information: [],
      google_user_data: [
        'google_account_information',
        'google_calendar_information',
        'google_contacts_information',
        'fitbit_information',
      ],
      information_you_provide: [],
    },
    'privacy_policy.data_minimization': {},
    'privacy_policy.data_retention_and_deletion': {},
    'privacy_policy.how_we_use_your_data': {
      automated_data_collection: [],
      dds_user_interface: [],
      google_data: ['dds_user_interface', 'survey_personalization', 'automated_data_collection'],
      survey_personalization: [],
    },
    'privacy_policy.introduction': {},
    'privacy_policy.security': {
      automatic_access_token_revocation: [],
      oauth: [],
    },
    'privacy_policy.sharing_and_disclosure': {
      google_data: [],
      legal_compliance: [],
      survey_platforms: [],
    },
  };

  return (
    <>
      <FormattedSections formattedSections={dataCollectionAndUseSections} />

      <Typography variant="h4">{t('privacy_policy.contact_information.title')}</Typography>
      <Typography paragraph>
        <Trans
          components={{
            email: (
              <Link
                color="primary"
                href={`mailto:${t('privacy_policy.contact_information.contact_email')}`}
                rel="noopener noreferrer"
              />
            ),
          }}
          i18nKey="privacy_policy.contact_information.content"
          values={{ contact_email: t('privacy_policy.contact_information.contact_email') }}
        />
      </Typography>
    </>
  );
}

function PagePrivacyPolicy(): JSX.Element {
  const { t } = useTranslation();

  return (
    <LayoutMain backUrl="/" header={<Typography variant="h3">{t('privacy_policy.title')}</Typography>}>
      <PrivacyPolicyContent />
    </LayoutMain>
  );
}

export default React.memo(PagePrivacyPolicy);
